import {
  arrayUtils,
  commonUtils,
  ListItemProps,
  moneyUtils,
  timeUtils,
} from '@sato/components';
import { setMonth } from 'date-fns';
import isAfter from 'date-fns/isAfter';
import { capitalize } from 'lodash';

import {
  Contract,
  ContractDepositStatus,
  ContractDepositType,
  ContractPaymentLine,
  ContractResponse,
  ContractStatusCode,
  ContractType,
} from '@models/contract.types';
import { LanguageKey } from '@models/Language';
import {
  ContractPartyResident,
  isContractPartyResident,
  isNonContractPartyResident,
  NonContractPartyResident,
} from '@models/Resident';
import { Nullable } from '@models/utility.types';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore, TranslatorFn } from '@stores/localization';
import { formatDate } from '@utils/date.utils';

export const contentfulKeysForContractStatusCodes: {
  [key in ContractStatusCode]: string;
} = {
  ACTIVE_STATUS: 'agreement_type_active',
  RESIGNED_STATUS: 'agreement_type_terminated',
  ENDED_STATUS: 'agreement_type_ended',
  ENDED_A_STATUS: 'agreement_type_ended_a',
  STARTING_STATUS: 'agreement_type_beginning',
  TERMINATION_PENDING: 'agreement_type_termination_pending',
};

export const contentfulKeysForContractTypes = {
  INDEFINITELY_TYPE: 'contractTypeIndefinitely',
  INDEFINITELY_WITH_END_DATE_TYPE: 'contractTypeIndefiniteWithEndDate',
  INDEFINITELY_AND_FIXED_PERIOD_TYPE: 'contractTypeIndefiniteWithFixedPeriod',
  FIXED_PERIOD_TYPE: 'contractTypePeriod',
} satisfies Record<ContractType, string>;

const contentfulKeyForDepositTypes = {
  MONEY: 'deposit_type_money',
  COMMITMENT: 'deposit_type_commitment',
  DEPOSIT: 'deposit_type_deposit',
  NO_DEPOSIT: 'deposit_type_no_deposit',
} satisfies Record<ContractDepositType, string>;

const contentfulKeyForDepositStatues = {
  IN_PROGRESS: 'deposit_status_in_progress',
  PAID: 'deposit_status_paid',
  NO_DEPOSIT: 'deposit_status_no_deposit',
  OPEN: 'deposit_status_open',
  COMPENSATED: 'deposit_status_compensated',
  EXEMPT: 'deposit_status_exempt',
  RECEIVED: 'deposit_status_received',
  COMPLETED: 'deposit_status_completed',
} satisfies Record<ContractDepositStatus, string>;

export const getContractDetails = ({
  t,
  isLoading,
  contract,
  language,
  selectedApartment,
}: {
  t: TranslatorFn;
  isLoading: boolean;
  contract: Contract | undefined;
  language: LanguageKey;
  selectedApartment: typeof apartmentStore.selectedApartment;
}) => {
  if (isLoading || !contract)
    return { tenants: [], occupants: [], details: [] };

  const {
    address,
    startDate,
    statusCodeOfContract,
    type,
    deposit,
    firstPaymentPeriod,
    firstTerminationDate,
    earliestPossibleEndDate,
  } = contract;

  const { tenants, occupants } = contract.residents.reduce<{
    tenants: ContractPartyResident[];
    occupants: NonContractPartyResident[];
  }>(
    (acc, curr) =>
      isContractPartyResident(curr)
        ? { ...acc, tenants: [...acc.tenants, curr] }
        : isNonContractPartyResident(curr)
          ? { ...acc, occupants: [...acc.occupants, curr] }
          : acc,
    { tenants: [], occupants: [] }
  );

  const firstPossibleEndDate = (() => {
    switch (type) {
      case 'INDEFINITELY_WITH_END_DATE_TYPE':
        return firstTerminationDate;
      case 'INDEFINITELY_AND_FIXED_PERIOD_TYPE':
        return earliestPossibleEndDate;
      default:
        return null;
    }
  })();

  return {
    tenants,
    occupants,
    details: [
      address && {
        label: t('apartment_address'),
        children: address,
      },

      startDate && {
        label: t('date_of_starting_rent'),
        children: formatDate(startDate),
      },

      statusCodeOfContract && {
        label: t('type_of_contract'),
        children: t(contentfulKeysForContractStatusCodes[statusCodeOfContract]),
      },

      type && {
        label: t('termination_of_contract'),
        children: t(contentfulKeysForContractTypes[type]),
      },

      commonUtils.nonNullable(firstPaymentPeriod) && {
        label: t('rentReviewMonth'),
        children: capitalize(
          timeUtils.formatDate({
            // firstPaymentPeriod months are 1-12, while dateFns wants months to be 0-11, so subtract by one.
            date: setMonth(new Date(), firstPaymentPeriod - 1),
            dateFormat: 'LLLL',
            locale: language,
          })
        ),
        tooltip: selectedApartment?.apartment.isAra
          ? t('rentReviewMonth.araTooltip')
          : t('rentReviewMonth.nonAraTooltip'),
      },

      firstPossibleEndDate && {
        label: t('termination_possibility'),
        children: `${timeUtils.formatDate({
          date: new Date(firstPossibleEndDate),
          dateFormat: timeUtils.DATE_FORMATS.DATE,
          locale: language,
        })} ${t('termination_starting_at')}`,
        tooltip: t('termination_possibility_tooltip'),
      },

      ...(deposit
        ? [
            deposit.depositType && {
              label: t('deposit_type_label'),
              children: t(contentfulKeyForDepositTypes[deposit.depositType]),
            },

            deposit.depositAmount && {
              label: t('deposit_amount'),
              children: moneyUtils.formatMoney(
                { value: deposit.depositAmount, currency: 'EUR' },
                localizationStore.language
              ),
            },

            deposit.depositStatus && {
              label: t('deposit_status_label'),
              children: t(
                contentfulKeyForDepositStatues[deposit.depositStatus]
              ),
            },
          ]
        : []),
    ].filter(commonUtils.isTruthy) satisfies ListItemProps[],
  };
};

export const getParkingSpacePaymentLine = (contract: Contract) =>
  contract.contractLines?.find((line) => line.type === 'PARKING_FEE');

export const isCurrentPayment = (line: ContractPaymentLine) => {
  const midnight = new Date();
  midnight.setHours(0);
  midnight.setMinutes(0);

  return (
    (!line.endDate ||
      timeUtils.isSameOrAfter(new Date(line.endDate), midnight)) &&
    timeUtils.isSameOrBefore(new Date(line.startDate))
  );
};

export const isFuturePayment = (line: ContractPaymentLine) => {
  return isAfter(new Date(line.startDate), new Date());
};

export const getContractPaymentLabel = (
  payment: ContractPaymentLine,
  t: TranslatorFn,
  isFutureList = false
): string => {
  const startDate = formatDate(payment.startDate);
  const endDate = payment.endDate ? formatDate(payment.endDate) : undefined;

  const perItemPrice = moneyUtils.formatMoney(
    {
      currency: 'EUR',
      value: payment.grossTotal / payment.quantity,
    },
    localizationStore.language
  );

  const formattedTimeRange = arrayUtils.joinArrayString([
    isFutureList && t('payments.starting_from'),
    arrayUtils.joinArrayString([startDate, endDate], ' – '),
  ]);

  switch (payment.type) {
    case 'RENT_REFUND':
      return `${t('payments.rent_refund')} (${formattedTimeRange})`;

    case 'SAUNA_FEE':
      return `${t('payments.sauna_bill')} (${formattedTimeRange}, ${t(
        'month_unit'
      )})`;

    case 'PARKING_FEE':
      return `${t(
        'payments.parking_bill'
      )} (${formattedTimeRange}, ${perItemPrice} ${t(
        'per_parking_space_per_month'
      )})`;

    case 'WATER_FEE':
      return `${t(
        'payments.water_bill'
      )} (${formattedTimeRange}, ${perItemPrice} ${t('per_person_per_month')})`;

    case 'RENT':
      return `${t('payments.rent')} (${formattedTimeRange}, ${t(
        'month_unit'
      )})`;

    case 'RENT_DISCOUNT':
      return `${t('payments.rent_reduction')} (${formattedTimeRange}, ${t(
        'month_unit'
      )})`;

    case 'EXTRA_RENT':
      return `${t('payments.additional_rent')} (${formattedTimeRange}, ${t(
        'month_unit'
      )})`;

    case 'WAREHOUSE_RENT':
      return `${t('payments.storage_rent')} (${formattedTimeRange}, ${t(
        'month_unit'
      )})`;
  }
};

/** Type guard for checking if a contract has the given status */
export const isContractStatus = <
  T extends ContractResponse,
  StatusT extends ContractStatusCode,
>(
  contract: Nullable<T>,
  status: StatusT
): contract is NonNullable<T> & {
  statusCodeOfContract: StatusT;
} =>
  !!contract?.statusCodeOfContract && contract?.statusCodeOfContract === status;

export const formatRentDueDate = (
  dueDate: number | string,
  { t, locale }: { t: TranslatorFn; locale: LanguageKey }
) => {
  if (dueDate) {
    let tempDate = dueDate.toString();
    const lastChar = tempDate.slice(-1);
    if (locale === 'en') {
      if (lastChar === '1') tempDate += 'st';
      else if (lastChar === '2') tempDate += 'nd';
      else if (lastChar === '3') tempDate += 'rd';
      else tempDate += 'th';

      return `${tempDate} ${t('agreements.every_month')}`;
    }
    return `${dueDate} ${t('agreements.every_month')}`;
  }
  return null;
};
